/**
 * LeadFormRenderer — the single source of truth for how a QR Studio lead form
 * looks and behaves. Rendered in two places:
 *
 *   1. the public landing page at fhl.one/f/{slug} (PublicLeadFormPage)
 *   2. the live phone-frame preview inside the QR Studio form builder
 *
 * Mobile-first on purpose: these pages are almost always opened by a phone
 * camera straight off a QR code. Inputs are 16px+ (prevents iOS zoom-on-focus),
 * touch targets are large, and the layout is a single column that scales up
 * gracefully on desktop.
 *
 * Props:
 *   form        — crm_lead_forms row (snake_case) or an equivalent draft object
 *   onSubmit    — async (answers, { hp }) => void; throw to surface an error
 *   isSubmitting— parent-controlled submit spinner state
 *   isPreview   — true inside the builder: fields are interactive but submit
 *                 is inert, so marketers can "feel" the form while editing
 */

import React, { useState } from 'react';
import {
  Box,
  Center,
  Circle,
  Container,
  VStack,
  Heading,
  Text,
  Button,
  Input,
  Textarea,
  Select,
  Checkbox,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Image,
  Avatar,
  useToast,
} from '@chakra-ui/react';
import { CheckIcon } from '@chakra-ui/icons';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Darken (negative) or lighten (positive) a hex color by `percent`. */
export function shadeHex(hex, percent) {
  const clean = /^#[0-9a-fA-F]{6}$/.test(hex || '') ? hex : '#1A365D';
  const num = parseInt(clean.slice(1), 16);
  const amt = Math.round(2.55 * percent);
  const clamp = (v) => Math.max(0, Math.min(255, v));
  const r = clamp((num >> 16) + amt);
  const g = clamp(((num >> 8) & 0xff) + amt);
  const b = clamp((num & 0xff) + amt);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

/** Client-side mirror of the server's validation — fast feedback, same rules. */
export function validateAnswers(fields, answers) {
  const errors = {};
  for (const field of fields || []) {
    const raw = answers[field.id];
    const value = typeof raw === 'string' ? raw.trim() : raw;
    const isEmpty = value === undefined || value === null || value === '' ||
      (field.type === 'checkbox' && value !== true);

    if (field.required && isEmpty) {
      errors[field.id] = 'This field is required';
      continue;
    }
    if (isEmpty) continue;

    if ((field.type === 'email' || field.mapTo === 'email') && !EMAIL_RE.test(String(value))) {
      errors[field.id] = 'Enter a valid email address';
    }
    if ((field.type === 'phone' || field.mapTo === 'phone') &&
        String(value).replace(/\D/g, '').length < 10) {
      errors[field.id] = 'Enter a valid phone number';
    }
  }
  return errors;
}

/**
 * Post-submit thank-you panel. Shared by the public page (post_submit_type
 * 'message') and the builder's test mode, so the simulation is pixel-true.
 */
export function SuccessPanel({ accent, title, body, minH = '100vh', children }) {
  return (
    <Center minH={minH} bg="gray.50" px={4}>
      <VStack
        spacing={5}
        bg="white"
        borderRadius="2xl"
        shadow="xl"
        borderWidth="1px"
        borderColor="gray.100"
        p={{ base: 8, md: 10 }}
        maxW="440px"
        w="100%"
        textAlign="center"
      >
        <Circle size="64px" style={{ backgroundColor: accent }}>
          <CheckIcon color="white" boxSize={7} />
        </Circle>
        <Heading size="md" color="gray.800">{title}</Heading>
        {body && <Text color="gray.600" lineHeight="1.6">{body}</Text>}
        {children}
        <Text fontSize="xs" color="gray.400" pt={2}>
          Future Home Loans — Equal Housing Lender
        </Text>
      </VStack>
    </Center>
  );
}

/**
 * Pull the CRM-mapped values back out of raw answers — used for booking-page
 * prefill (?name=&email=&phone=) by the public page and the test mode.
 */
export function extractContactPrefill(fields, answers) {
  let firstName = '';
  let lastName = '';
  let email = '';
  let phone = '';
  for (const field of fields || []) {
    const value = answers[field.id];
    if (value === undefined || value === null || value === '') continue;
    const str = String(value).trim();
    if (field.mapTo === 'firstName' && !firstName) firstName = str;
    if (field.mapTo === 'lastName' && !lastName) lastName = str;
    if (field.mapTo === 'fullName' && !firstName) {
      // LEGACY — builder no longer offers fullName; kept for old field defs.
      const parts = str.split(/\s+/);
      firstName = parts[0] || '';
      lastName = parts.slice(1).join(' ');
    }
    if (field.mapTo === 'email' && !email) email = str;
    if (field.mapTo === 'phone' && !phone) phone = str;
  }
  return { name: `${firstName} ${lastName}`.trim(), email, phone };
}

function autoCompleteFor(field) {
  switch (field.mapTo) {
    case 'firstName': return 'given-name';
    case 'lastName': return 'family-name';
    case 'fullName': return 'name';
    case 'email': return 'email';
    case 'phone': return 'tel';
    default: return 'on';
  }
}

function FieldInput({ field, value, accent, onChange }) {
  const common = {
    size: 'lg',
    fontSize: '16px', // never below 16px — iOS Safari zooms on focus otherwise
    bg: 'gray.50',
    borderColor: 'gray.200',
    focusBorderColor: accent,
    borderRadius: 'lg',
    autoComplete: autoCompleteFor(field),
  };

  if (field.type === 'textarea') {
    return (
      <Textarea
        {...common}
        rows={3}
        placeholder={field.placeholder || ''}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  if (field.type === 'select') {
    return (
      <Select
        {...common}
        placeholder={field.placeholder || 'Select an option'}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
      >
        {(field.options || []).map((option) => (
          <option key={option} value={option}>{option}</option>
        ))}
      </Select>
    );
  }
  if (field.type === 'checkbox') {
    return (
      <Checkbox
        size="lg"
        colorScheme="blue"
        isChecked={value === true}
        onChange={(e) => onChange(e.target.checked)}
        sx={{ '.chakra-checkbox__control': { borderColor: 'gray.300' } }}
      >
        <Text fontSize="15px" color="gray.600">{field.placeholder || 'Yes'}</Text>
      </Checkbox>
    );
  }

  const typeAttrs =
    field.type === 'email' ? { type: 'email', inputMode: 'email' } :
    field.type === 'phone' ? { type: 'tel', inputMode: 'tel' } :
    field.type === 'date' ? { type: 'date' } :
    { type: 'text' };

  return (
    <Input
      {...common}
      {...typeAttrs}
      placeholder={field.placeholder || ''}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export default function LeadFormRenderer({ form, onSubmit, isSubmitting = false, isPreview = false }) {
  const toast = useToast();
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [hp, setHp] = useState(''); // honeypot — humans never see or fill it

  const accent = /^#[0-9a-fA-F]{6}$/.test(form?.accent_color || '') ? form.accent_color : '#1A365D';
  const accentDark = shadeHex(accent, -18);
  const fields = Array.isArray(form?.fields) ? form.fields : [];

  const setAnswer = (fieldId, value) => {
    setAnswers((prev) => ({ ...prev, [fieldId]: value }));
    setErrors((prev) => {
      if (!prev[fieldId]) return prev;
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isPreview) return;

    const validationErrors = validateAnswers(fields, answers);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      await onSubmit(answers, { hp });
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: err?.message || 'Please try again.',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    }
  };

  return (
    <Box minH={isPreview ? '100%' : '100vh'} bg="gray.50">
      {/* Accent hero */}
      <Box
        pt={{ base: 10, md: 14 }}
        pb={{ base: 16, md: 20 }}
        px={4}
        textAlign="center"
        style={{ background: `linear-gradient(135deg, ${accent} 0%, ${accentDark} 100%)` }}
      >
        <VStack spacing={4} maxW="560px" mx="auto">
          {form?.logo_url ? (
            <Image
              src={form.logo_url}
              alt=""
              maxH="64px"
              maxW="220px"
              objectFit="contain"
              borderRadius="md"
            />
          ) : form?.lo_name ? (
            <Avatar name={form.lo_name} size="lg" borderWidth="3px" borderColor="whiteAlpha.700" />
          ) : null}
          <Heading
            size="lg"
            color="white"
            lineHeight="1.25"
            fontWeight="bold"
            letterSpacing="-0.01em"
          >
            {form?.headline || form?.title || 'Let\u2019s connect'}
          </Heading>
          {form?.intro_text && (
            <Text color="whiteAlpha.900" fontSize={{ base: 'md', md: 'lg' }} lineHeight="1.55">
              {form.intro_text}
            </Text>
          )}
        </VStack>
      </Box>

      {/* Form card overlapping the hero */}
      <Container maxW="480px" px={4} mt={{ base: -10, md: -12 }} pb={10}>
        <Box
          as="form"
          onSubmit={handleSubmit}
          bg="white"
          borderRadius="2xl"
          shadow="xl"
          borderWidth="1px"
          borderColor="gray.100"
          p={{ base: 5, md: 7 }}
          noValidate
        >
          <VStack spacing={5} align="stretch">
            {fields.map((field) => (
              <FormControl key={field.id} isInvalid={!!errors[field.id]} isRequired={field.required}>
                <FormLabel fontSize="sm" fontWeight="semibold" color="gray.700" mb={1.5}>
                  {field.label}
                </FormLabel>
                <FieldInput
                  field={field}
                  value={answers[field.id]}
                  accent={accent}
                  onChange={(value) => setAnswer(field.id, value)}
                />
                <FormErrorMessage>{errors[field.id]}</FormErrorMessage>
              </FormControl>
            ))}

            {/* Honeypot — visually hidden, bots fill it, server silently drops */}
            <Input
              value={hp}
              onChange={(e) => setHp(e.target.value)}
              name="website"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              position="absolute"
              left="-9999px"
              h="1px"
              w="1px"
              opacity={0}
            />

            <Button
              type="submit"
              size="lg"
              h="54px"
              fontSize="17px"
              borderRadius="xl"
              color="white"
              isLoading={isSubmitting}
              loadingText="Sending..."
              style={{ backgroundColor: accent }}
              _hover={{ opacity: 0.92 }}
              _active={{ opacity: 0.85 }}
              shadow="md"
            >
              {form?.button_text || 'Submit'}
            </Button>

            {form?.consent_text && (
              <Text fontSize="xs" color="gray.400" textAlign="center" lineHeight="1.5">
                {form.consent_text}
              </Text>
            )}
          </VStack>
        </Box>

        {/* Compliance footer */}
        <VStack spacing={1} mt={6}>
          {(form?.lo_name || form?.lo_nmls) && (
            <Text fontSize="xs" color="gray.400" textAlign="center">
              {form?.lo_name || 'Loan Officer'}
              {form?.lo_title ? ` — ${form.lo_title}` : ''}
              {form?.lo_nmls ? ` — NMLS #${form.lo_nmls}` : ''}
            </Text>
          )}
          <Text fontSize="xs" color="gray.400" textAlign="center">
            Future Home Loans — Equal Housing Lender
          </Text>
        </VStack>
      </Container>
    </Box>
  );
}
